import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Users, Filter } from 'lucide-react';
import { UserFollowButton } from '@/components/feed/UserFollowButton';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { supabaseUserService } from '@/services/supabaseUserService';
import { internalUserService } from '@/services/internalUserService';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useNavigate } from 'react-router-dom';
import { SecureProfilePhoto } from '@/components/profile/SecureProfilePhoto';
import { useIsMobile } from '@/hooks/useIsMobile';

interface SearchableUser {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role: string;
  profile_photo_url?: string;
  user_type: 'supabase' | 'internal';
}

const FindPeople: React.FC = () => {
  const { user, supabaseUser, internalUser } = useAuthenticatedUser();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [userTypeFilter, setUserTypeFilter] = useState<string>('all');
  const [searchResults, setSearchResults] = useState<SearchableUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const currentUserId = user?.id;
  const currentUserType = supabaseUser ? 'supabase' : 'internal';

  const handleSearch = async () => {
    if (!user) return;
    
    setLoading(true);
    setHasSearched(true);
    
    try {
      // Clean search query by removing @ symbol if present
      const cleanQuery = searchQuery.replace(/^@/, '').trim();

      // Run both queries in parallel for better performance
      const [supabaseUsers, internalUsers] = await Promise.all([
        (userTypeFilter === 'all' || userTypeFilter === 'supabase')
          ? (cleanQuery ? supabaseUserService.searchUsers(cleanQuery) : supabaseUserService.getAllUsers())
          : Promise.resolve([]),
        (userTypeFilter === 'all' || userTypeFilter === 'internal')
          ? (cleanQuery ? internalUserService.searchUsers(cleanQuery) : internalUserService.getAllUsers())
          : Promise.resolve([])
      ]);

      // Combine and format results
      const allUsers: SearchableUser[] = [
        ...supabaseUsers.map(user => ({ ...user, user_type: 'supabase' as const })),
        ...internalUsers.map(user => ({ ...user, user_type: 'internal' as const }))
      ];

      // Filter by role if specified
      let filteredUsers = allUsers;
      if (roleFilter !== 'all') {
        filteredUsers = allUsers.filter(user => user.role === roleFilter);
      }

      // Exclude current user
      filteredUsers = filteredUsers.filter(user => 
        !(user.id === currentUserId && user.user_type === currentUserType)
      );

      setSearchResults(filteredUsers);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Mark initial load complete when user is ready (don't auto-fetch all users)
  useEffect(() => {
    if (user) {
      setInitialLoadComplete(true);
    }
  }, [user]);

  // Debounced search - trigger search when user types
  useEffect(() => {
    if (user && searchQuery.trim().length > 0) {
      const timeoutId = setTimeout(() => {
        handleSearch();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, roleFilter, userTypeFilter]);

  if (!user) {
    return (
      <>
        {isMobile ? <MobileHeader /> : <Header />}
        <div className="min-h-screen bg-background pt-20 pb-20">
          <main className="container mx-auto p-6">
            <Card className="bg-card border-border">
              <CardContent className="p-6 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Please Log In</h2>
                <p className="text-muted-foreground">
                  You need to be logged in to discover people to follow
                </p>
              </CardContent>
            </Card>
          </main>
        </div>
        <div className="hidden md:block">
          <Footer />
        </div>
        <div className="md:hidden">
          <MobileBottomNav />
        </div>
      </>
    );
  }

  const getRoleColor = (role: string): string => {
    const roleColors: { [key: string]: string } = {
      'player': 'bg-blue-100 text-blue-800',
      'coach': 'bg-green-100 text-green-800',
      'venue': 'bg-purple-100 text-purple-800',
      'team-owner': 'bg-orange-100 text-orange-800',
      'scorekeeper': 'bg-yellow-100 text-yellow-800',
      'fan': 'bg-pink-100 text-pink-800',
      'parent': 'bg-indigo-100 text-indigo-800',
      'admin': 'bg-red-100 text-red-800'
    };
    return roleColors[role] || 'bg-gray-100 text-gray-800';
  };

  const getDisplayName = (user: SearchableUser): string => {
    if (user.username) return user.username;
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    return 'Unknown User';
  };

  return (
    <>
      {isMobile ? <MobileHeader /> : <Header />}
      <div className="min-h-screen bg-background pt-20 pb-20">
        <main className="container mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                Find People to Follow
              </h1>
              <p className="text-muted-foreground mt-2">
                Discover players, coaches, venues, and other users to follow
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/feed')}
            >
              Back to Feed
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search & Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Input
                  placeholder="Search by name or username (try @username)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="player">Players</SelectItem>
                  <SelectItem value="coach">Coaches</SelectItem>
                  <SelectItem value="venue">Venues</SelectItem>
                  <SelectItem value="team-owner">Team Owners</SelectItem>
                  <SelectItem value="scorekeeper">Scorekeepers</SelectItem>
                  <SelectItem value="fan">Fans</SelectItem>
                  <SelectItem value="parent">Parents</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} disabled={loading}>
                <Search className="h-4 w-4 mr-2" />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
            {!hasSearched && initialLoadComplete && (
              <div className="mt-4 text-center">
                <Button variant="outline" onClick={handleSearch} disabled={loading}>
                  <Users className="h-4 w-4 mr-2" />
                  Browse All Users
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Search Results */}
        <div className="space-y-4">
          {loading && (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Searching for users...</p>
              </CardContent>
            </Card>
          )}

          {!loading && !hasSearched && initialLoadComplete && (
            <Card>
              <CardContent className="p-6 text-center">
                <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Search for People</h3>
                <p className="text-muted-foreground">
                  Enter a name or username above, or click "Browse All Users" to discover people
                </p>
              </CardContent>
            </Card>
          )}

          {!loading && hasSearched && searchResults.length === 0 && (
            <Card>
              <CardContent className="p-6 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Users Found</h3>
                <p className="text-muted-foreground">
                  Try adjusting your search criteria or browse all users
                </p>
              </CardContent>
            </Card>
          )}

          {!loading && searchResults.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.map((user) => (
                <Card key={`${user.user_type}-${user.id}`} className="bg-card border-border hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <SecureProfilePhoto
                        filePath={user.profile_photo_url}
                        fallbackInitials={getDisplayName(user).substring(0, 2).toUpperCase()}
                        size="lg"
                        className="h-20 w-20"
                      />
                      <div className="flex-1 min-w-0 text-right">
                        <h3 className="font-semibold text-lg truncate">
                          {getDisplayName(user)}
                        </h3>
                        <div className="flex justify-end mt-1 mb-3">
                          <Badge variant="secondary" className={getRoleColor(user.role)}>
                            {user.role}
                          </Badge>
                        </div>
                        <div className="flex justify-end">
                          <UserFollowButton
                            targetUserId={user.id}
                            targetUserType={user.user_type}
                            currentUserId={currentUserId}
                            currentUserType={currentUserType}
                            targetUserName={getDisplayName(user)}
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        </main>
      </div>
      <div className="hidden md:block">
        <Footer />
      </div>
      <div className="md:hidden">
        <MobileBottomNav />
      </div>
    </>
  );
};

export default FindPeople;